import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, CheckCircle2, XCircle, Coffee, Clock, ArrowRight, Cake, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const kpis = [
  { key: "total_employees", label: "Total employees", icon: Users, tone: "neutral" },
  { key: "present", label: "Present today", icon: CheckCircle2, tone: "present" },
  { key: "halfday", label: "Half day today", icon: Clock, tone: "halfday" },
  { key: "absent", label: "Absent today", icon: XCircle, tone: "absent" },
  { key: "leave", label: "On leave today", icon: Coffee, tone: "leave" },
];

function toneClass(tone) {
  switch (tone) {
    case "present": return "text-[hsl(var(--status-present))]";
    case "halfday": return "text-[hsl(var(--status-halfday))]";
    case "absent":  return "text-[hsl(var(--status-absent))]";
    case "leave":   return "text-[hsl(var(--status-leave))]";
    default:        return "text-foreground";
  }
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, todayRes, upRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/birthdays/today"),
          api.get("/birthdays/upcoming?days=30"),
        ]);
        setStats(statsRes.data);
        setBirthdaysToday(todayRes.data.birthdays || []);
        setUpcoming((upRes.data.upcoming || []).filter((u) => u.days_until > 0));

        // Only surface a toast on first mount when there are birthdays today
        const today = todayRes.data.birthdays || [];
        if (today.length > 0) {
          const key = `bday-toast-${todayRes.data.date}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            const first = today[0];
            const rest = today.length - 1;
            toast.success(
              rest > 0
                ? `${first.name} + ${rest} more have a birthday today!`
                : `It's ${first.name}'s birthday today!`,
              { icon: "🎂", duration: 6000 }
            );
          }
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-6xl" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Overview</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
            Today at a glance
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {stats?.date ? new Date(stats.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}
          </p>
        </div>
        <Link
          to="/presently/attendance"
          data-testid="quick-mark-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand text-white text-sm font-medium btn-lift shadow-soft hover:shadow-soft-lg"
        >
          Mark today's attendance
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10" data-testid="dashboard-kpis">
        {kpis.map(({ key, label, icon: Icon, tone }) => (
          <Card
            key={key}
            data-testid={`kpi-${key}`}
            className="p-5 rounded-2xl border-border bg-card shadow-soft btn-lift hover:shadow-soft-lg"
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono-mini">{label}</div>
              <Icon className={`h-4 w-4 ${toneClass(tone)}`} />
            </div>
            <div className={`font-display text-5xl font-bold tabular-nums tracking-tighter mt-4 ${toneClass(tone)}`}>
              {loading ? "—" : (stats?.[key] ?? 0)}
            </div>
          </Card>
        ))}
      </div>

      {/* Birthday banner */}
      {!loading && birthdaysToday.length > 0 && (
        <Card
          className="mt-8 p-6 rounded-2xl border-border relative overflow-hidden shadow-soft"
          data-testid="birthday-banner"
          style={{ background: "hsl(var(--brand-peach) / 0.18)" }}
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-2xl grid place-items-center shadow-soft" style={{ background: "hsl(var(--status-leave))" }}>
              <PartyPopper className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold tracking-tight">
                {birthdaysToday.length === 1
                  ? `It's ${birthdaysToday[0].name}'s birthday today`
                  : `${birthdaysToday.length} birthdays today`}
                <span className="ml-1"> 🎂</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Don't forget to send warm wishes.
              </div>
            </div>
            <div className="flex -space-x-2">
              {birthdaysToday.slice(0, 6).map((b) => {
                const initials = (b.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <Avatar key={b.employee_id} className="h-9 w-9 ring-2 ring-card" title={`${b.name}${b.turning != null ? ` · turns ${b.turning}` : ""}`}>
                    <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
                  </Avatar>
                );
              })}
              {birthdaysToday.length > 6 && (
                <div className="h-9 w-9 rounded-full bg-muted grid place-items-center text-[11px] ring-2 ring-card">
                  +{birthdaysToday.length - 6}
                </div>
              )}
            </div>
          </div>
          <ul className="mt-4 flex flex-wrap gap-2" data-testid="birthday-list">
            {birthdaysToday.map((b) => (
              <li
                key={b.employee_id}
                data-testid={`birthday-today-${b.employee_id}`}
                className="text-xs px-2.5 py-1 rounded-full border border-border bg-card"
              >
                <span className="font-medium">{b.name}</span>
                {b.turning != null && <span className="text-muted-foreground"> · turns {b.turning}</span>}
                <span className="text-muted-foreground"> · {b.department}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Upcoming birthdays */}
      {!loading && upcoming.length > 0 && (
        <Card className="mt-6 p-6 rounded-2xl border-border shadow-soft" data-testid="upcoming-birthdays">
          <div className="flex items-center gap-2">
            <Cake className="h-4 w-4 text-muted-foreground" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Upcoming birthdays · next 30 days</div>
          </div>
          <ul className="mt-4 divide-y divide-border">
            {upcoming.slice(0, 6).map((b) => {
              const initials = (b.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
              const nextDate = new Date(b.next_birthday);
              const label = nextDate.toLocaleDateString(undefined, { month: "short", day: "numeric" });
              return (
                <li
                  key={b.employee_id}
                  data-testid={`birthday-upcoming-${b.employee_id}`}
                  className="py-3 flex items-center gap-3"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{b.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {b.department} · {b.designation}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono-mini tabular-nums">{label}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {b.days_until === 1 ? "tomorrow" : `in ${b.days_until} days`}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Unmarked banner */}
      {!loading && stats && stats.total_employees > 0 && stats.unmarked > 0 && (
        <Card className="mt-8 p-5 rounded-2xl border-border flex items-center justify-between gap-4 shadow-soft">
          <div>
            <div className="text-sm font-medium">
              {stats.unmarked} {stats.unmarked === 1 ? "employee is" : "employees are"} still unmarked for today
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Head to the attendance page to finish the roll call.
            </div>
          </div>
          <Link
            to="/presently/attendance"
            data-testid="unmarked-cta"
            className="text-sm font-medium text-brand hover:opacity-75 transition-opacity"
          >
            Continue →
          </Link>
        </Card>
      )}

      {!loading && stats && stats.total_employees === 0 && (
        <Card className="mt-8 p-10 rounded-2xl border-dashed border-2 border-border text-center bg-card/60">
          <div className="font-display text-2xl font-bold">No employees yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Add your first employee to start tracking attendance.
          </p>
          <Link
            to="/presently/employees"
            data-testid="empty-add-employee-cta"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-xl bg-brand text-white text-sm font-medium btn-lift shadow-soft"
          >
            Add an employee <ArrowRight className="h-4 w-4" />
          </Link>
        </Card>
      )}
    </div>
  );
}
